import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useUser } from '../contexts/UserContext';
import { Typography, Container, Box, CircularProgress, Alert, Avatar, Button, LinearProgress, IconButton, Tooltip } from '@mui/material';
import { Event as EventIcon, LocationOn as LocationOnIcon, HourglassEmpty, MoneyOff, CheckCircle, Done, People, AttachMoney, Refresh as RefreshIcon, Campaign as AnnouncementIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import EventCalendar from './EventCalendar';
import JoinModal from './JoinModal';
import AnnouncementModal from './AnnouncementModal';
import { Event } from '../types/Event';
import { COLORS } from '../constants/colors';
import { PAGINATION } from '../constants/pagination';
import { 
  getEventDateInfo, 
  getDateColor, 
  shouldShowMonthHeader, 
  isLastEventInMonth,
} from '../utils/dateHelpers';
import {
  HistoryEvent,
  paginateEvents,
  calculateTotalPages,
  needsApproval,
  isUnpaid,
  isPaid,
  isCheckedIn
} from '../utils/eventHelpers';

const UserHome: React.FC = () => {
  const { loading: userLoading, participantHistory, refreshParticipantHistory } = useUser();

  const [error, setError] = useState<string>('');
  const [page, setPage] = useState<number>(PAGINATION.DEFAULT_PAGE);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loadingEvent, setLoadingEvent] = useState<boolean>(false);

  // Parse payment link to extract valid URL
  const parsePaymentLink = (link: string | undefined): string | null => {
    if (!link) return null;
    
    // Try to find URL patterns in the text
    const urlPatterns = [
      // Full URLs with protocol
      /(https?:\/\/[^\s]+)/i,
      // URLs without protocol
      /((?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/i,
      // PayMe specific patterns
      /(payme\.hsbc\/[^\s]+)/i,
      /(payme\.hsbc\.com\.hk\/[^\s]+)/i
    ];
    
    for (const pattern of urlPatterns) {
      const match = link.match(pattern);
      if (match) {
        let url = match[1];
        
        // Add protocol if missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        
        // Remove trailing punctuation
        url = url.replace(/[.,;!?]+$/, '');
        
        return url;
      }
    }
    
    // If no pattern matched but looks like a URL, try to use it
    const trimmed = link.trim();
    if (trimmed.includes('.') || trimmed.includes('/')) {
      let url = trimmed;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      return url;
    }
    
    return null;
  };
  const [announcementModalOpen, setAnnouncementModalOpen] = useState<boolean>(false);
  const [selectedEventForAnnouncements, setSelectedEventForAnnouncements] = useState<Event | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pullDistance, setPullDistance] = useState<number>(0);
  const touchStartY = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async (showRefreshIndicator = false) => {
    try {
      setError('');
      if (showRefreshIndicator) setIsRefreshing(true);
      await refreshParticipantHistory();
    } catch (e: any) {
      setError(e.response?.data?.message || '載入會員資料失敗');
    } finally {
      if (showRefreshIndicator) {
        // Keep the indicator visible for a moment so user sees the refresh happened
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  }, [refreshParticipantHistory]);

  // Refresh on component mount
  useEffect(() => {
    if (!userLoading) {
      refresh();
    }
  }, [userLoading, refresh]);

  // Refresh when window regains focus (user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      refresh();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh]);

  // Pull-to-refresh handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const container = containerRef.current;
    if (container && container.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const container = containerRef.current;
    if (container && container.scrollTop === 0 && touchStartY.current > 0) {
      const touchY = e.touches[0].clientY;
      const distance = touchY - touchStartY.current;
      
      if (distance > 0) {
        setPullDistance(Math.min(distance, 60));
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance > 40) {
      refresh(true);
    }
    setPullDistance(0);
    touchStartY.current = 0;
  }, [pullDistance, refresh]);

  // Sort all events from latest to oldest
  const sortedEvents = useMemo(() => {
    return [...participantHistory].sort((a, b) => 
      new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime()
    );
  }, [participantHistory]);

  const paginatedEvents = useMemo(() => {
    return paginateEvents(sortedEvents, page, PAGINATION.EVENTS_PER_PAGE);
  }, [sortedEvents, page]);

  const totalPages = calculateTotalPages(sortedEvents.length, PAGINATION.EVENTS_PER_PAGE);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStatusIcon = (event: HistoryEvent) => {
    const iconProps = { fontSize: '1rem', color: 'white' } as const;
    const avatarSize = { width: 28, height: 28 };

    if (needsApproval(event)) {
      return (
        <Avatar sx={{ ...avatarSize, bgcolor: COLORS.STATUS.PENDING_APPROVAL }}>
          <HourglassEmpty sx={iconProps} />
        </Avatar>
      );
    }
    if (isUnpaid(event)) {
      return (
        <Avatar sx={{ ...avatarSize, bgcolor: COLORS.STATUS.UNPAID }}>
          <MoneyOff sx={iconProps} />
        </Avatar>
      );
    }
    if (isPaid(event) && !isCheckedIn(event)) {
      return (
        <Avatar sx={{ ...avatarSize, bgcolor: COLORS.STATUS.PAID }}>
          <CheckCircle sx={iconProps} />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ ...avatarSize, bgcolor: COLORS.STATUS.CHECKED_IN }}>
        <Done sx={iconProps} />
      </Avatar>
    );
  };

  const handleEventClick = async (historyEvent: HistoryEvent) => {
    try {
      setLoadingEvent(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/events/${historyEvent.eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(res.data);
    } catch (e: any) {
      setError(e.response?.data?.message || '載入活動詳情失敗');
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleAnnouncementClick = async (historyEvent: HistoryEvent) => {
    try {
      setLoadingEvent(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/events/${historyEvent.eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEventForAnnouncements(res.data);
      setAnnouncementModalOpen(true);
    } catch (e: any) {
      setError(e.response?.data?.message || '載入活動詳情失敗');
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const handleEventUpdated = () => {
    refresh();
  };

  const handleAddToCalendar = (event: HistoryEvent) => {
    // Create ICS file content
    const formatDateForICS = (dateStr: string, timeStr: string) => {
      const date = new Date(dateStr);
      const [startTime] = timeStr.split('-').map(t => t.trim());
      const [hours, minutes] = startTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateTime = formatDateForICS(event.eventDate, event.eventTime);
    const endTime = event.eventTime.split('-')[1]?.trim() || event.eventTime.split('-')[0]?.trim();
    const endDateTime = formatDateForICS(event.eventDate, endTime);

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Ball Game//Event//EN',
      'BEGIN:VEVENT',
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${event.eventTitle}`,
      `LOCATION:${event.eventLocation}`,
      `DESCRIPTION:${event.sendDetails || ''}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${event.eventTitle}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (userLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container 
      ref={containerRef}
      maxWidth="md" 
      sx={{ px: 4, py: 1, pb: 10, position: 'relative', overflow: 'auto' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {pullDistance > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -40 + pullDistance,
            left: 0,
            right: 0,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: pullDistance === 0 ? 'top 0.3s ease' : 'none',
          }}
        >
          <RefreshIcon 
            sx={{ 
              fontSize: 32,
              color: pullDistance > 40 ? 'primary.main' : 'text.secondary',
              transform: `rotate(${pullDistance * 3.6}deg)`,
              transition: 'color 0.2s ease',
            }} 
          />
        </Box>
      )}
      
      {/* Refreshing indicator */}
      {isRefreshing && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
          <LinearProgress />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      )}
      <>
        {sortedEvents.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            gap: 2
          }}>
            <EventIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
              目前沒有任何活動
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center' }}>
              當您加入活動後，會顯示在這裡
            </Typography>
          </Box>
        ) : (
          <>
            {/* Calendar View */}
            <Box sx={{ pb: 1 }}>
              <EventCalendar 
                events={sortedEvents} 
                onEventClick={handleEventClick}
              />
            </Box>

            {/* List View - Timeline Style */}
            <Box sx={{ pt: 2, pb: 1, display: 'flex', flexDirection: 'column' }}>
              {(() => {
                const now = new Date();
                const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                let todayMarkerShown = false;
                
                return paginatedEvents.map((ev, idx) => {
                  const dateInfo = getEventDateInfo(ev.eventDate);
                  const dateColor = getDateColor(dateInfo.isSunday, dateInfo.isSaturday);
                  const isToday = ev.eventDate === today;
                  
                  const prevMonth = idx > 0 ? getEventDateInfo(paginatedEvents[idx - 1].eventDate).month : null;
                  const nextMonth = idx < paginatedEvents.length - 1 ? getEventDateInfo(paginatedEvents[idx + 1].eventDate).month : null;
                  
                  const showMonthHeader = shouldShowMonthHeader(dateInfo.month, prevMonth, idx === 0);
                  const isLastInMonth = isLastEventInMonth(dateInfo.month, nextMonth, idx === paginatedEvents.length - 1);
                  
                  // Check if next event will show a month header
                  const nextShowsMonthHeader = idx < paginatedEvents.length - 1 && nextMonth &&
                    shouldShowMonthHeader(nextMonth, dateInfo.month, false);
                  
                  // Check if we need to show "Today" marker (only for non-today events)
                  // Since events are sorted from latest to oldest (newest first)
                  const prevDate = idx > 0 ? paginatedEvents[idx - 1].eventDate : null;
                  const currentDate = ev.eventDate;
                  const nextDate = idx < paginatedEvents.length - 1 ? paginatedEvents[idx + 1].eventDate : null;
                  
                  // Show "Today" marker if event is NOT today and:
                  // 1. Today falls between the previous event (future) and this event (past), OR
                  // 2. This is the first event and it's in the past (all events are past)
                  let showTodayMarker = false;
                  if (!todayMarkerShown && !isToday) {
                    if (prevDate && prevDate > today && currentDate < today) {
                      // Previous event is in future, current is in past - today is between them
                      showTodayMarker = true;
                      todayMarkerShown = true;
                    } else if (idx === 0 && currentDate < today) {
                      // First event is in the past, show today before it
                      showTodayMarker = true;
                      todayMarkerShown = true;
                    }
                  }
                  
                  // Check if next item is the today marker
                  const nextIsTodayMarker = !todayMarkerShown && nextDate && !isToday && (
                    (currentDate > today && nextDate < today) ||
                    (idx === paginatedEvents.length - 1 && currentDate > today)
                  );
                
                return (
                  <React.Fragment key={`${ev.eventId}-${idx}`}>
                    {showMonthHeader && (
                      <Box sx={{ 
                        mb: 2, 
                        mt: idx === 0 ? 0 : 3,
                        pl: { xs: 0, sm: 0 }
                      }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                          color: 'text.primary',
                          borderBottom: '2px solid #e0e0e0',
                          pb: 0.5
                        }}>
                          {dateInfo.fullMonthYear}
                        </Typography>
                      </Box>
                    )}
                    {showTodayMarker && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: { xs: 1.25, sm: 1.5 },
                          alignItems: 'center',
                          position: 'relative',
                          py: 2,
                          mb: { xs: 2.5, sm: 3 },
                          height: '40px'
                        }}
                      >
                        {/* Date column with timeline continuation */}
                        <Box sx={{ 
                          width: { xs: '56px', sm: '64px' },
                          flexShrink: 0,
                          position: 'relative',
                          height: '100%',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            right: { xs: '-9px', sm: 0 },
                            top: '-20px',
                            bottom: '-40px',
                            width: '2px',
                            bgcolor: '#e0e0e0',
                            display: { xs: 'block', sm: 'none' }
                          }
                        }} />
                        
                        {/* Full width horizontal line - positioned absolutely to span entire width */}
                        <Box sx={{ 
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: '50%',
                          height: '2px',
                          bgcolor: 'primary.main',
                          zIndex: 1
                        }} />
                        
                        {/* Centered "今天" text - positioned absolutely */}
                        <Box sx={{ 
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2
                        }}>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            color: 'primary.main',
                            bgcolor: 'background.paper',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            whiteSpace: 'nowrap',
                            display: 'block'
                          }}>
                            今天
                          </Typography>
                        </Box>
                        
                        {/* Right column spacer */}
                        <Box sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: { xs: 1.25, sm: 1.5 }, 
                        mb: nextIsTodayMarker ? 0 : { xs: 2.5, sm: 3 },
                        position: 'relative',
                        '&:last-child': { mb: 0 }
                      }}
                    >
                    {/* Date/Time Section - Left */}
                    <Box sx={{ 
                      width: { xs: '56px', sm: '64px' },
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 1,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: { xs: '-9px', sm: 0 },
                        top: 0,
                        bottom: isLastInMonth ? 0 : { xs: '-40px', sm: 0 },
                        width: '2px',
                        bgcolor: '#e0e0e0',
                        display: { xs: 'block', sm: 'none' }
                      }
                    }}>
                      {isToday && (
                        <Typography variant="caption" sx={{
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          color: 'white',
                          bgcolor: '#f44336',
                          px: 0.75,
                          // py: 0.25,
                          borderRadius: 1,
                          // mb: 0.5
                        }}>
                          今天
                        </Typography>
                      )}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: isToday ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                        borderRadius: '50%',
                        width: { xs: '52px', sm: '60px' },
                        height: { xs: '52px', sm: '60px' },
                        justifyContent: 'center',
                        pt: 0.5
                      }}>
                        <Typography variant="h3" sx={{ 
                          fontWeight: 700, 
                          lineHeight: 1, 
                          color: dateColor, 
                          fontSize: { xs: '2rem', sm: '2.5rem' }
                        }}>
                          {dateInfo.day}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: dateColor, 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' }, 
                          lineHeight: 1.2,
                          fontWeight: 500
                        }}>
                          {dateInfo.dayOfWeek}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                        color: 'text.primary', 
                        mt: { xs: 0.25, sm: 0.5 }
                      }}>
                        {ev.eventTime.split('-')[0]?.trim()}
                      </Typography>
                      
                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, maxWidth: { xs: '52px', sm: '60px' }, flexWrap: 'wrap' }}>
                        {ev.hasAnnouncements && (
                          <Tooltip title="查看公告" arrow>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnnouncementClick(ev);
                              }}
                              sx={{ 
                                p: 0.4,
                                bgcolor: 'action.hover',
                                '&:hover': { bgcolor: 'action.selected' },
                                minWidth: 0,
                                width: { xs: '24px', sm: '28px' },
                                height: { xs: '24px', sm: '28px' }
                              }}
                            >
                              <AnnouncementIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="加入行事曆" arrow>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCalendar(ev);
                            }}
                            sx={{ 
                              p: 0.4,
                              bgcolor: 'action.hover',
                              '&:hover': { bgcolor: 'action.selected' },
                              minWidth: 0,
                              width: { xs: '24px', sm: '28px' },
                              height: { xs: '24px', sm: '28px' }
                            }}
                          >
                            <CalendarIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Event Information - Right */}
                    <Box 
                      onClick={() => handleEventClick(ev)}
                      sx={{ 
                        flex: 1,
                        borderBottom: (nextIsTodayMarker || nextShowsMonthHeader) ? 'none' : `0.5px solid #e0e0e0`,
                        pt: isToday ? { xs: 2, sm: 2.5 } : 0,
                        pb: isToday ? { xs: 4, sm: 4.5 } : { xs: 2, sm: 2 },
                        pl: { xs: 2, sm: 2 },
                        minWidth: 0,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between', 
                        gap: 1,
                        position: 'relative'
                      }}>
                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 700, 
                          lineHeight: 1.3, 
                          flex: 1,
                          fontSize: { xs: '0.95rem', sm: '1rem' }
                        }}>
                          {(ev as any).siteName || ev.eventTitle}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                          {renderStatusIcon(ev)}
                        </Box>
                      </Box>
                      
                      {ev.sendDetails && (
                        <Box sx={{ mb: { xs: 0.5, sm: 0.75 } }}>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            fontSize: { xs: '0.8rem', sm: '0.85rem' },
                            lineHeight: 1.5,
                          }}>
                            {ev.sendDetails}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: { xs: 0.4, sm: 0.5 } }}>
                        <EventIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {ev.eventTime}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: { xs: 0.4, sm: 0.5 } }}>
                        <LocationOnIcon sx={{ 
                          fontSize: { xs: '0.9rem', sm: '1rem' }, 
                          color: 'text.secondary', 
                          mt: 0.1, 
                          flexShrink: 0 
                        }} />
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                          lineHeight: 1.4 
                        }}>
                          {ev.eventLocation}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <People sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            {`報名${ev.spots}位 | ${ev.totalParticipants || 0}/${ev.maxParticipants || 0}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AttachMoney sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            ${ev.fee}
                          </Typography>
                        </Box>
                        {!ev.paid && parsePaymentLink(ev.paymentLink) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              const url = parsePaymentLink(ev.paymentLink);
                              if (url) {
                                window.open(url, '_blank');
                              }
                            }}
                            sx={{
                              borderColor: COLORS.STATUS.UNPAID,
                              color: COLORS.STATUS.UNPAID,
                              '&:hover': { 
                                borderColor: '#d32f2f',
                                bgcolor: 'rgba(244, 67, 54, 0.04)'
                              },
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              py: 0.6,
                              px: 1,
                              m: -0.45,
                              minWidth: 'auto',
                              textTransform: 'none',
                              fontWeight: 600,
                              lineHeight: 1.2,
                              height: 'auto'
                            }}
                          >
                            Pay Now
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  </React.Fragment>
                );
              });
              })()}
            </Box>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                <Button 
                  variant="outlined" 
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  size="small"
                >
                  上一頁
                </Button>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  第 {page} / {totalPages} 頁
                </Typography>
                <Button 
                  variant="outlined" 
                  disabled={page === totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  size="small"
                >
                  下一頁
                </Button>
              </Box>
            )}
          </>
        )}
      </>
      
      {/* Loading Event Details */}
      {loadingEvent && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          bgcolor: 'rgba(0,0,0,0.3)', 
          zIndex: 2000 
        }}>
          <CircularProgress />
        </Box>
      )}

      {/* Announcement Modal */}
      <AnnouncementModal
        open={announcementModalOpen}
        onClose={() => setAnnouncementModalOpen(false)}
        announcements={selectedEventForAnnouncements?.announcements || []}
        eventTitle={
          typeof selectedEventForAnnouncements?.site === 'object' 
            ? selectedEventForAnnouncements?.site?.name 
            : undefined
        }
        isParticipant={true}
      />

      {/* Event Details Modal */}
      {selectedEvent && (
        <JoinModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onEventUpdated={handleEventUpdated}
          viewOnly={true}
          fromUserHome={true}
        />
      )}
    </Container>
  );
};

export default UserHome;
